import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, RotateCcw, Copy, Share2, Twitter, ArrowRight, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, RESULTS, tallyScores, track, setClaimedNation } from "../lib/quiz";
import { setMyNation } from "../lib/auth";
import { logQuizCompletion } from "../lib/api";
import { SITE } from "../lib/content";
import SubscribeForm from "../components/site/SubscribeForm";

const TOTAL = QUESTIONS.length;

// -----------------------------------------------------------------------------
// Sub-views
// -----------------------------------------------------------------------------

function IntroView({ onStart }) {
  return (
    <motion.section
      key="intro"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 text-center"
      data-testid="quiz-intro"
    >
      <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] justify-center">
        <span>The Rite of Belonging</span>
      </div>
      <h1
        data-testid="quiz-title"
        className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1.02] mt-6"
      >
        What Nation Has
        <br />
        <span className="text-[#C5A059] italic font-light">
          Claimed You?
        </span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-[#A39E99] leading-relaxed max-w-2xl mx-auto">
        Answer the call of Bakhtak. Your instincts, flaws, loyalties, and hidden truths will decide where you belong.
      </p>
      <p className="mt-4 text-xs tracking-[0.22em] uppercase text-[#737373]">
        {TOTAL} questions · ~3 minutes
      </p>
      <button
        type="button"
        onClick={onStart}
        data-testid="quiz-begin-btn"
        className="mt-10 inline-flex items-center gap-2 bg-[#C5A059] text-black px-9 py-4 text-xs tracking-[0.28em] uppercase hover:bg-[#D4AF68] transition-colors active:scale-[0.98]"
      >
        Begin the Rite <ArrowRight size={14} />
      </button>
      <div className="mt-12 grid grid-cols-3 md:grid-cols-9 gap-2 max-w-lg mx-auto">
        {Object.values(RESULTS).map((r) => (
          <div
            key={r.slug}
            className="block aspect-square border overflow-hidden bg-[#0A0A0A]"
            title={r.nation}
            style={{ borderColor: `${r.accent}55` }}
            data-testid={`quiz-symbol-${r.slug}`}
          >
            {r.symbol ? (
              <img
                src={r.symbol}
                alt={`${r.nation} sigil`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div
                className="w-full h-full"
                style={{ backgroundColor: `${r.accent}1A` }}
              />
            )}
          </div>
        ))}
      </div>
    </motion.section>
  );
}

function QuestionView({ index, question, onAnswer, onBack }) {
  const progress = ((index + 1) / TOTAL) * 100;
  return (
    <motion.section
      key={question.id}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-3xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-16"
      data-testid={`quiz-question-${index + 1}`}
    >
      {/* Progress + back */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={onBack}
          disabled={index === 0}
          data-testid="quiz-back-btn"
          className="text-xs tracking-[0.22em] uppercase text-[#A39E99] inline-flex items-center gap-1 hover:text-[#C5A059] disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Go back to previous question"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#737373]">
          Question {index + 1} of {TOTAL}
        </span>
      </div>
      <div
        className="w-full h-[2px] bg-white/10 mb-10 overflow-hidden"
        role="progressbar"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={TOTAL}
        data-testid="quiz-progress"
      >
        <motion.div
          className="h-full bg-[#C5A059]"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      <h2
        className="font-display text-2xl sm:text-3xl md:text-4xl text-[#EAE0D5] leading-[1.15] mb-8 md:mb-10"
        data-testid="quiz-question-prompt"
      >
        {question.prompt}
      </h2>

      <ul className="flex flex-col gap-2.5" role="list">
        {question.answers.map((a, i) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onAnswer(a)}
              data-testid={`quiz-answer-${index + 1}-${a.id}`}
              className="w-full text-left bg-[#0A0A0A] border border-white/10 hover:border-[#C5A059]/50 hover:bg-[#0F0F0F] active:scale-[0.995] transition-all duration-200 px-5 md:px-7 py-4 md:py-5 flex items-start gap-4 group"
            >
              <span className="shrink-0 mt-0.5 inline-flex w-6 h-6 items-center justify-center border border-[#C5A059]/40 text-[10px] tracking-[0.18em] text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-black transition-colors">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-[15px] md:text-base text-[#EAE0D5] leading-relaxed">
                {a.text}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

function RevealView() {
  return (
    <motion.section
      key="reveal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6"
      data-testid="quiz-reveal"
    >
      <div className="text-[10px] tracking-[0.5em] uppercase text-[#C5A059] mb-6">
        Bakhtak Has Spoken
      </div>
      <motion.h2
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="font-display italic text-3xl md:text-5xl text-[#EAE0D5] max-w-2xl"
      >
        The chain you carry has a name…
      </motion.h2>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 1.2 }}
        className="mt-10"
      >
        <div className="w-10 h-10 border border-[#C5A059]/30 rotate-45 animate-pulse" />
      </motion.div>
    </motion.section>
  );
}

function ResultView({ result, hybridResult, onRestart, totals }) {
  const r = result;
  const isHybrid = Boolean(hybridResult);

  // share text
  const url = typeof window !== "undefined" ? `${window.location.origin}/nation-quiz` : "";
  const shareText = isHybrid
    ? `I stand between ${r.nation} and ${hybridResult.nation} in Chains Beneath Bakhtak. Take the quiz and find your nation.`
    : `I was claimed by ${r.nation} — ${r.title} — in Chains Beneath Bakhtak. Take the quiz and find your nation.`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${url}`);
      toast.success("Result copied to clipboard.");
    } catch {
      toast.error("Could not copy. Try the share button instead.");
    }
  };

  const onShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Chains Beneath Bakhtak — Nation Quiz",
          text: shareText,
          url,
        });
        track("clicked_share_result", { nation: r.slug });
      } catch {
        /* user cancelled */
      }
    } else {
      onCopy();
    }
  };

  const onTwitter = () => {
    const t = encodeURIComponent(`${shareText} ${url}`);
    window.open(`https://twitter.com/intent/tweet?text=${t}`, "_blank", "noopener");
    track("clicked_share_twitter", { nation: r.slug });
  };

  // Generate a downloadable PNG badge — pure canvas, no API.
  const onDownloadBadge = () => {
    const W = 1080, H = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Dark background
    ctx.fillStyle = "#050505"; ctx.fillRect(0, 0, W, H);
    // Subtle radial vignette via accent
    const accent = r.accent || "#C5A059";
    const grad = ctx.createRadialGradient(W/2, H*0.42, 80, W/2, H*0.42, W*0.7);
    grad.addColorStop(0, accent + "33");
    grad.addColorStop(1, "#05050500");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

    // Frame
    ctx.strokeStyle = accent + "AA"; ctx.lineWidth = 3;
    ctx.strokeRect(60, 60, W - 120, H - 120);
    ctx.strokeStyle = accent + "44"; ctx.lineWidth = 1;
    ctx.strokeRect(80, 80, W - 160, H - 160);

    // Eyebrow
    ctx.fillStyle = accent;
    ctx.font = "600 26px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("CHAINS BENEATH BAKHTAK", W/2, 175);
    ctx.fillStyle = "#A39E99";
    ctx.font = "400 18px 'Inter', sans-serif";
    ctx.fillText("THE NATION RITE", W/2, 215);

    // "I AM"
    ctx.fillStyle = "#EAE0D5";
    ctx.font = "300 60px 'Cormorant Garamond', 'Georgia', serif";
    ctx.fillText("I am claimed by", W/2, 360);

    // Nation name (large)
    ctx.fillStyle = accent;
    ctx.font = "italic 300 160px 'Cormorant Garamond', 'Georgia', serif";
    ctx.fillText(r.nation, W/2, 540);

    // Title strip
    ctx.fillStyle = "#A39E99";
    ctx.font = "500 24px 'Inter', sans-serif";
    ctx.fillText((r.title || "").toUpperCase(), W/2, 600);

    // Lower quote
    ctx.fillStyle = "#EAE0D5";
    ctx.font = "italic 400 32px 'Cormorant Garamond', 'Georgia', serif";
    const quoteLines = (r.tagline || r.shortDescription || "").match(/.{1,46}(\s|$)/g) || [];
    let y = 740;
    for (const line of quoteLines.slice(0, 3)) {
      ctx.fillText(line.trim(), W/2, y);
      y += 44;
    }

    // Footer URL
    ctx.fillStyle = accent;
    ctx.font = "500 22px 'Inter', sans-serif";
    ctx.fillText("CHAINSBENEATHBAKHTAK.COM / NATION-QUIZ", W/2, H - 130);

    // Download
    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bakhtak-${r.slug}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      track("downloaded_nation_badge", { nation: r.slug });
      toast.success("Nation badge downloaded. Post it.");
    }, "image/png");
  };

  // top 3 nations for the result chart
  const top3 = useMemo(() => {
    const max = Math.max(...Object.values(totals));
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([slug, v]) => ({
        slug,
        pct: max ? Math.round((v / max) * 100) : 0,
        nation: RESULTS[slug],
      }));
  }, [totals]);

  return (
    <motion.section
      key="result"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-20"
      data-testid="quiz-result"
    >
      <div className="ornate-rule text-[10px] tracking-[0.42em] uppercase justify-center" style={{ color: r.accent }}>
        <span>Bakhtak Has Spoken</span>
      </div>

      <h1
        className="font-display text-center text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-6"
        data-testid="quiz-result-nation"
      >
        {isHybrid ? (
          <>
            You stand between
            <br />
            <span style={{ color: r.accent }} className="italic font-light">
              {r.nation}
            </span>{" "}
            <span className="text-[#A39E99] text-3xl md:text-5xl">&</span>{" "}
            <span style={{ color: hybridResult.accent }} className="italic font-light">
              {hybridResult.nation}
            </span>
          </>
        ) : (
          <>
            You belong to{" "}
            <span style={{ color: r.accent }} className="italic font-light">
              {r.nation}
            </span>
          </>
        )}
      </h1>
      <p
        className="text-center text-base md:text-xl italic mt-5 max-w-2xl mx-auto"
        style={{ color: `${r.accent}` }}
        data-testid="quiz-result-hook"
      >
        "{isHybrid
          ? "The chain forks. Two nations claim a part of you."
          : r.hook}"
      </p>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12 mt-12 md:mt-16">
        {/* Nation portrait */}
        <div className="md:col-span-5">
          <div
            className="border"
            style={{ borderColor: `${r.accent}40`, boxShadow: `0 0 80px -25px ${r.accent}` }}
          >
            <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
              {r.image ? (
                <img
                  src={r.image}
                  alt={r.nation}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs tracking-widest">
                  ARTWORK PLACEHOLDER
                </div>
              )}
            </div>
            <div className="bg-[#0A0A0A] px-5 py-4 border-t" style={{ borderColor: `${r.accent}30` }}>
              <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: r.accent }}>
                {r.title}
              </div>
              <div className="font-display text-2xl text-[#EAE0D5] mt-1">{r.nation}</div>
            </div>
          </div>

          {/* Top-3 distribution */}
          <div className="mt-8 bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99] mb-4">
              The pull on you
            </div>
            <ul className="space-y-3" data-testid="quiz-result-top3">
              {top3.map((t) => (
                <li key={t.slug}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#EAE0D5]">{t.nation.nation}</span>
                    <span className="text-[#737373]">{t.pct}%</span>
                  </div>
                  <div className="w-full h-[3px] bg-white/5">
                    <div
                      className="h-full"
                      style={{ width: `${t.pct}%`, backgroundColor: t.nation.accent }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Description + actions */}
        <div className="md:col-span-7">
          <p
            className="text-base md:text-lg text-[#A39E99] leading-relaxed"
            data-testid="quiz-result-description"
          >
            {r.description}
          </p>

          {isHybrid && (
            <p className="text-sm text-[#A39E99] mt-5 italic">
              You also carry the marks of {hybridResult.nation}.{" "}
              <button
                type="button"
                onClick={onRestart}
                className="link-gold underline-offset-2 hover:underline"
              >
                Retake the rite
              </button>{" "}
              or read both nations to see which speaks louder.
            </p>
          )}

          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: r.accent }}>
                Strengths
              </div>
              <ul className="flex flex-wrap gap-2">
                {r.strengths.map((s) => (
                  <li
                    key={s}
                    className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 border"
                    style={{ borderColor: `${r.accent}50`, color: r.accent }}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase mb-3 text-[#A39E99]">
                Shadow
              </div>
              <p className="text-sm text-[#A39E99] leading-relaxed">{r.shadow}</p>
            </div>
          </div>

          <div className="mt-8 bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">
              The friend you probably are
            </div>
            <p className="text-base text-[#EAE0D5] mt-2 italic">"{r.friendRole}"</p>
          </div>

          <div className="mt-6 bg-[#0A0A0A] border border-white/5 p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">
              First character to meet
            </div>
            <p className="font-display text-2xl text-[#EAE0D5] mt-2">{r.firstCharacter}</p>
            <p className="text-xs text-[#737373] mt-1">From the cast of Chains Beneath Bakhtak.</p>
          </div>

          {/* Archive teaser — connect the quiz nation to ongoing weekly drops */}
          <Link
            to="/archive"
            onClick={() => track("clicked_archive_from_quiz", { nation: r.slug })}
            data-testid="quiz-cta-archive"
            className="group mt-6 block border p-5 md:p-6 bg-[#0A0A0A] hover:bg-[#0F0F0F] transition-colors"
            style={{ borderColor: `${r.accent}55`, boxShadow: `0 0 60px -28px ${r.accent}` }}
          >
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-10 h-10 border flex items-center justify-center mt-0.5"
                style={{ borderColor: `${r.accent}66`, color: r.accent }}
              >
                <ScrollText size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: r.accent }}>
                  The Bakhtak Archive
                </div>
                <p className="font-display text-xl md:text-2xl text-[#EAE0D5] mt-1.5 leading-tight">
                  Meet the faces of{" "}
                  <span style={{ color: r.accent }} className="italic font-light">
                    {r.nation}
                  </span>
                  .
                </p>
                <p className="text-sm text-[#A39E99] mt-2 leading-relaxed">
                  A sealed record surfaces every seven days. Guess the nation, unlock the figure, add them to the cast — permanently, for everyone.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase group-hover:gap-3 transition-all" style={{ color: r.accent }}>
                  Open the Archive <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </Link>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/nations"
              data-testid="quiz-cta-explore-nation"
              onClick={() => track("clicked_explore_nation_from_quiz", { nation: r.slug })}
              className="bg-[#C5A059] text-black px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF68] transition-colors inline-flex items-center justify-center gap-2"
            >
              Explore {r.nation}
            </Link>
            <Link
              to="/pilot"
              data-testid="quiz-cta-read-pilot"
              onClick={() => track("clicked_read_pilot_from_quiz", { nation: r.slug })}
              className="border border-[#C5A059]/40 text-[#C5A059] px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#C5A059]/10 inline-flex items-center justify-center"
            >
              Read the Pilot
            </Link>
            {SITE.socials.discord && /^https?:\/\//i.test(SITE.socials.discord) && (
              <a
                href={SITE.socials.discord}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="quiz-cta-join-discord"
                onClick={() => track("clicked_join_discord_from_quiz", { nation: r.slug })}
                className="border border-white/10 text-[#EAE0D5] px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:border-[#C5A059]/40 inline-flex items-center justify-center"
              >
                Join Discord
              </a>
            )}
          </div>

          {/* Inline email capture — peak engagement moment */}
          <div
            className="mt-8 bg-[#0A0A0A] border p-6 md:p-7"
            style={{ borderColor: `${r.accent}40`, boxShadow: `0 0 36px -20px ${r.accent}` }}
            data-testid="quiz-result-subscribe"
          >
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: r.accent }}>
              Want first-look art from the {r.nation} arc?
            </div>
            <p className="text-sm text-[#A39E99] mt-2 mb-4 leading-relaxed">
              Drop your email — we&apos;ll send you sketches, lore drops, and the moment the pilot opens.
            </p>
            <SubscribeForm
              source={`quiz-${r.slug}`}
              testIdPrefix="quiz-result-sub"
            />
            <p className="text-[10px] text-[#737373] mt-3">
              No spam. One whisper per chapter.
            </p>
          </div>

          {/* Share row */}
          <div className="mt-8 pt-6 border-t border-white/5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99] mb-3">
              Share your nation
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onShareNative}
                data-testid="quiz-share-native"
                className="inline-flex items-center gap-2 border border-white/10 text-[#EAE0D5] px-4 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-[#C5A059]/40"
              >
                <Share2 size={14} /> Share
              </button>
              <button
                type="button"
                onClick={onCopy}
                data-testid="quiz-share-copy"
                className="inline-flex items-center gap-2 border border-white/10 text-[#EAE0D5] px-4 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-[#C5A059]/40"
              >
                <Copy size={14} /> Copy
              </button>
              <button
                type="button"
                onClick={onTwitter}
                data-testid="quiz-share-twitter"
                className="inline-flex items-center gap-2 border border-white/10 text-[#EAE0D5] px-4 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-[#C5A059]/40"
              >
                <Twitter size={14} /> X / Twitter
              </button>
              <button
                type="button"
                onClick={onDownloadBadge}
                data-testid="quiz-download-badge"
                className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-4 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF68]"
              >
                <ArrowRight size={14} /> Save Nation Badge (PNG)
              </button>
              <button
                type="button"
                onClick={onRestart}
                data-testid="quiz-restart"
                className="inline-flex items-center gap-2 border border-white/10 text-[#A39E99] px-4 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-[#C5A059]/40 hover:text-[#EAE0D5]"
              >
                <RotateCcw size={14} /> Retake
              </button>
            </div>
            <p className="text-[11px] text-[#737373] mt-4 leading-relaxed">
              On Instagram? Take a screenshot of this page — it's built to look good. Tag us so we see it.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export default function QuizPage() {
  const [stage, setStage] = useState("intro"); // intro | quiz | reveal | result
  const [index, setIndex] = useState(0);
  const [selections, setSelections] = useState([]); // [{qid, aid, scores}]

  const start = () => {
    setIndex(0);
    setSelections([]);
    setStage("quiz");
    track("nation_quiz_started");
  };

  const onAnswer = (answer) => {
    const next = [...selections];
    next[index] = {
      qid: QUESTIONS[index].id,
      aid: answer.id,
      scores: answer.scores,
    };
    setSelections(next);

    if (index + 1 < TOTAL) {
      setIndex(index + 1);
    } else {
      // finished
      setStage("reveal");
      setTimeout(() => setStage("result"), 1900);
    }
  };

  const onBack = () => {
    if (index > 0) setIndex(index - 1);
  };

  const restart = () => {
    setStage("intro");
    setIndex(0);
    setSelections([]);
  };

  // Compute result when we transition into result stage
  const computed = useMemo(() => {
    if (stage !== "result" && stage !== "reveal") return null;
    return tallyScores(selections);
  }, [stage, selections]);

  // Fire completion + result events once
  useEffect(() => {
    if (stage === "result" && computed?.primary) {
      setClaimedNation(computed.primary);
      // If user is signed in, sync their nation to the backend so the Nation Signal works.
      setMyNation(computed.primary);
      // Log the completion for EVERY user (signed-in or anonymous) so event
      // stats reflect real reach — was previously invisible for guests.
      logQuizCompletion(computed.primary, computed.hybrid || null);
      track("nation_quiz_completed", { nation: computed.primary, hybrid: computed.hybrid || null });
      track(`nation_result_${computed.primary}`);
    }
  }, [stage, computed]);

  return (
    <div data-testid="quiz-page" className="min-h-[80vh]">
      <AnimatePresence mode="wait">
        {stage === "intro" && <IntroView onStart={start} />}
        {stage === "quiz" && (
          <QuestionView
            index={index}
            question={QUESTIONS[index]}
            onAnswer={onAnswer}
            onBack={onBack}
          />
        )}
        {stage === "reveal" && <RevealView />}
        {stage === "result" && computed && (
          <ResultView
            result={RESULTS[computed.primary]}
            hybridResult={computed.hybrid ? RESULTS[computed.hybrid] : null}
            onRestart={restart}
            totals={computed.totals}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
